/**
 * Clears the validation state of every connection field that does not apply to the values on screen (Option C, C0).
 *
 * TanStack derives `isFieldsValid` / `canSubmit` from EVERY field's `errorMap`, mounted or not, and `handleSubmit()`
 * returns before validating when `canSubmit` is false. An error left behind by a mode the user has since left (SSH
 * switched off, Oracle TNS chosen, another auth type) therefore blocked Save with nothing on screen (B8). Reconciling
 * runs on every value change — a user's or a patch's — and again inside the Test / Load / Save gate.
 *
 * It reads and subscribes to the form's BASE store, never the derived `form.store`: `@tanstack/store` 0.7 does not
 * recompute a derived store again for a write made inside a derived listener in the same flush (it keeps the stale
 * `fieldMeta` / `isFieldsValid`), while a base-store listener runs before the derived stores recompute.
 */
import { useEffect } from 'react';
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import { FIELD_RULES } from './connectionFieldRules';

type MetaEntry = { errorMap?: Record<string, unknown>; isValidating?: boolean } | undefined;

export type ReconcilableForm = {
  baseStore: {
    state: { values: Partial<ConnectionFormValues>; fieldMetaBase: Record<string, MetaEntry> };
    subscribe: (listener: () => void) => () => void;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack's setter is generic over deep field paths
  setFieldMeta: (field: any, updater: (previous: any) => any) => void;
};

function hasValidationState(entry: MetaEntry): boolean {
  if (!entry) return false;
  return entry.isValidating === true || Object.values(entry.errorMap ?? {}).some((error) => error !== undefined && error !== null && error !== false);
}

export function reconcileFieldMeta(form: ReconcilableForm): void {
  const { values, fieldMetaBase } = form.baseStore.state;
  for (const rule of FIELD_RULES) {
    if (rule.applies(values) || !hasValidationState(fieldMetaBase[rule.field])) continue;
    form.setFieldMeta(rule.field, (previous: Record<string, unknown> | undefined) => ({ ...previous, errorMap: {}, isValidating: false }));
  }
}

/** Reconciles after every change of the form's values, whatever made it (typing, a toggle, a patch, a reset). */
export function useModeReconciliation(form: ReconcilableForm): void {
  useEffect(() => {
    let last = form.baseStore.state.values;
    return form.baseStore.subscribe(() => {
      const values = form.baseStore.state.values;
      if (values === last) return;
      last = values;
      reconcileFieldMeta(form);
    });
  }, [form]);
}
